import React, { useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';

const AsyncDropdown = ({
  label = '',
  placeholder = 'Select an option',
  value,
  onChange,
  loadOptions, // async (searchTerm) => Promise<Array<{ label, value }>>
  minimumInputLength = 2,
  disabled = false,
  className = '',
  dropdownClassName = '',
  optionClassName = '',
  searchPlaceholder = 'Search...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedTerm = useDebounce(searchTerm, 300);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Load options dynamically
  useEffect(() => {
    let isCancelled = false;
    const fetchOptions = async () => {
      if (!isOpen) return;
      const term = debouncedTerm.trim();
      if (term.length < minimumInputLength) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const results = await loadOptions(term);
        if (!isCancelled) {
          setOptions(Array.isArray(results) ? results : []);
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };
    fetchOptions();
    return () => {
      isCancelled = true;
    };
  }, [debouncedTerm, isOpen, loadOptions, minimumInputLength]);

  const selectedLabel = () => {
    const found = options.find((opt) => opt.value === value);
    return found ? found.label : placeholder;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-[16px] font-medium text-[#000000] mb-2">{label}</label>
      )}
      <div
        className={`w-full px-4 bg-white border border-[#E9E9E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 text-[16px] font-medium cursor-pointer flex items-center justify-between ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${className} py-2.5`}
        onClick={() => {
          if (!disabled) setIsOpen((prev) => !prev);
        }}
      >
        <span className="truncate">{selectedLabel()}</span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className={`absolute z-50 w-full mt-1 bg-white border border-[#E9E9E9] rounded-lg shadow-lg max-h-60 overflow-hidden ${dropdownClassName}`}>
          <div className="p-2 border-b border-[#E9E9E9]">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full px-3 py-2 text-sm border border-[#E9E9E9] rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-500">Searching…</div>
            ) : options.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">No options found</div>
            ) : (
              options.map((option) => (
                <div
                  key={option.value}
                  className={`px-4 py-3 text-[16px] font-medium cursor-pointer hover:bg-gray-50 transition-colors duration-150 ${
                    value === option.value ? 'bg-blue-50 text-blue-600' : 'text-[#000000]'
                  } ${optionClassName}`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AsyncDropdown;

